<section class="go-container">
  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <!-- Intro -->
        <p class="go-body-copy">The Go Accordion Component should be used when multiple sets of data need to be rendered, but vertical space is a concern.</p>
  
        <p class="go-body-copy">
          The <code class="code-block--inline">go-accordion</code> element must be accompanied by
          <code class="code-block--inline">go-accordion-panel</code> children.
          See <a [routerLink]="['/ui-kit/accordion/panel']">Accordion Panel</a> for details.
        </p>
      </div>

      <div class="go-column go-column--100">
        <!-- Bindings -->
        <h3 class="go-heading-3">Bindings</h3>
        <code [highlight]="componentBindings"></code>
      </div>

      <div class="go-column go-column--100">
        <!-- borderless -->
        <h4 class="go-heading-4">borderless</h4>
        <p class="go-body-copy">
          <code class="code-block--inline">borderless</code> will render the panel without borders.
        </p>
      </div>

      <div class="go-column go-column--100">
        <!-- boxShadow -->
        <h4 class="go-heading-4">boxShadow</h4>
        <p class="go-body-copy">
          <code class="code-block--inline">boxShadow</code> will render the panel with box-shadows.
          <br/>
          <caption>
            Hint: This can be used in conjunction with <code class="code-block--inline">borderless</code> to create a
            "card" like experience.
          </caption>
        </p>
      </div>

      <div class="go-column go-column--100">
        <!-- expandAll -->
        <h4 class="go-heading-4">expandAll</h4>
        <p class="go-body-copy">
          <code class="code-block--inline">expandAll</code>
          will ensure each panel in the accordion is expanded on first render.
          <code class="code-block--inline">expandAll</code> will also override
          <code class="code-block--inline">multiExpand</code> and set it to true.
        </p>
      </div>
      
      <div class="go-column go-column--100">
        <!-- multiExpand -->
        <h4 class="go-heading-4">multiExpand</h4>
        <p class="go-body-copy">
          <code class="code-block--inline">multiExpand</code>
          will allow for multiple panels to be expanded at any given time.
        </p>
      </div>

      <div class="go-column go-column--100">
        <!-- showIcons -->
        <h4 class="go-heading-4">showIcons</h4>
        <p class="go-body-copy">
          This will enable each panel to have an icon shown to the left of the heading.
        </p>
        <caption>
          Note: even if the <code class="code-block--inline">icon</code> binding is passed to an
          <code class="code-block--inline">accordion-panel</code>, it will not be displayed unless
          <code class="code-block--inline">showIcons</code> is set to <code class="code-block--inline">true</code>.
          This is to encourage a consistent UI. If one <code class="code-block--inline">accordion-panel</code>
          has an icon, they all should.
        </caption>
      </div>

      <div class="go-column go-column--100">
        <!-- slim -->
        <h4 class="go-heading-4">slim</h4>
        <p class="go-body-copy">
          <code class="code-block--inline">slim</code>
          will reduce the height of the accordion panel, making it slimmer vertically.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <!-- theme -->
        <h4 class="go-heading-4">theme</h4>
        <code [highlight]="themeOptions"></code>
        <p class="go-body-copy">
          This will set the appearance of the <code class="code-block--inline">accordion</code>. <code class="code-block--inline">light</code>
          is the default <code class="code-block--inline">theme</code> and should be used in most places. The only time the
          <code class="code-block--inline">dark</code> theme should be used is when the accordion
          lives inside a UI element that has a dark themed background by default. (ex: off-canvas)
        </p>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="default">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Default</h2>
      <go-copy cardId="default" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="defaultExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion>
            <go-accordion-panel heading="Test 1" [expanded]="true">
              This is some content for <b>Test 1</b>.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2">
              This is a second thing. 
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3">
              This is a third thing. 
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="borderless">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Borderless</h2>
      <go-copy cardId="borderless" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="borderlessExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion [borderless]="true">
            <go-accordion-panel heading="Test 1">
              This is some content for Test 1.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2">
              This is a second thing.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3">
              This is a third thing.
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="box-shadow">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Box Shadow</h2>
      <go-copy cardId="box-shadow" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="boxShadowExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion [boxShadow]="true">
            <go-accordion-panel heading="Test 1">
              This is some content for Test 1.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2">
              This is a second thing.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3">
              This is a third thing.
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>


  <go-card class="go-column go-column--100" id="expand-all">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Expand All</h2>
      <go-copy cardId="expand-all" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="expandAllExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion [expandAll]="true">
            <go-accordion-panel heading="Test 1">
              This is some content for <b>Test 1</b>.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2">
              This is a second thing. 
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3">
              This is a third thing. 
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="multiple-expansion">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Multiple Expansion</h2>
      <go-copy cardId="multiple-expansion" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="multiExpandExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion [multiExpand]="true">
            <go-accordion-panel heading="Test 1" [expanded]="true">
              This is some content for <b>Test 1</b>.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2" [expanded]="true">
              This is a second thing. 
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3">
              This is a third thing. 
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="with-icons">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">With Icons</h2>
      <go-copy cardId="with-icons" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="showIconsExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion [showIcons]="true">
            <go-accordion-panel heading="Test 1" icon="home" [expanded]="true">
              This is some content for <b>Test 1</b>.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2" icon="settings">
              This is a second thing. 
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3" icon="landscape">
              This is a third thing. 
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="slim">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Slim</h2>
      <go-copy cardId="slim" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="slimExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion [slim]="true">
            <go-accordion-panel heading="Test 1">
              This is some content for <b>Test 1</b>.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2">
              This is a second thing. 
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3">
              This is a third thing. 
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="theme">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Theme</h2>
      <go-copy cardId="theme" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="themeExample"></code>
        </div>
        <div class="go-column go-column--50">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-accordion theme="dark">
            <go-accordion-panel heading="Test 1">
              This is some content for <b>Test 1</b>.
            </go-accordion-panel>
            <go-accordion-panel heading="Test 2">
              This is a second thing. 
            </go-accordion-panel>
            <go-accordion-panel heading="Test 3">
              This is a third thing. 
            </go-accordion-panel>
          </go-accordion>
        </div>
      </div>
    </div>
  </go-card>
</section>
